import React from 'react';
import { MoveDiagonal2 } from 'lucide-react';
import type { CanvasSectionBlock } from '../sectionRegistry';
import type { TransformHandle } from './types';
import { resolveBlockPolicy } from './blockPolicies/resolveBlockPolicy';

interface SelectionOverlayProps {
  block: CanvasSectionBlock;
  enabled: boolean;
  isSelected: boolean;
  isPrimarySelection?: boolean;
  multiSelectActive?: boolean;
  showControls?: boolean;
  showResizeControls?: boolean;
  muted?: boolean;
  scaleMode?: boolean;
  overlayStyle?: React.CSSProperties;
  overlayInsetPx?: number;
  overlayRef?: React.Ref<HTMLDivElement>;
  onBeginTransform: (
    event: React.PointerEvent<HTMLButtonElement>,
    block: CanvasSectionBlock,
    handle: TransformHandle
  ) => void;
  onMoveTransform: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onEndTransform: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onBeginScale?: (
    event: React.PointerEvent<HTMLButtonElement>,
    block: CanvasSectionBlock,
    axis: 'x' | 'y' | 'both'
  ) => void;
  onScaleMove?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onScaleEnd?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onStartTextEdit?: (blockId: string) => void;
  label?: string;
}

const CORNER_HANDLE_CLASS =
  'pointer-events-auto absolute z-30 inline-flex h-2.5 w-2.5 items-center justify-center rounded-[3px] border border-blue-500 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.24)]';

const EDGE_HANDLE_CLASS =
  'pointer-events-auto absolute z-30 inline-flex items-center justify-center rounded-full border border-blue-500 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.24)]';

const OVERLAY_INSET_PX = 8;
const FRAME_HIT_THICKNESS_PX = 10;

function offsetStyleValue(
  value: React.CSSProperties[keyof React.CSSProperties] | undefined,
  deltaPx: number
) {
  if (typeof value === 'number') {
    return value + deltaPx;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const operator = deltaPx >= 0 ? '+' : '-';
    return `calc(${value} ${operator} ${Math.abs(deltaPx)}px)`;
  }
  return value;
}

function expandOverlayStyle(style?: React.CSSProperties, insetPx = OVERLAY_INSET_PX): React.CSSProperties {
  if (!style) {
    return { inset: -insetPx };
  }

  if (style.inset !== undefined) {
    return {
      ...style,
      inset: offsetStyleValue(style.inset, -insetPx),
    };
  }

  return {
    ...style,
    left: offsetStyleValue(style.left, -insetPx),
    top: offsetStyleValue(style.top, -insetPx),
    right: offsetStyleValue(style.right, -insetPx),
    bottom: offsetStyleValue(style.bottom, -insetPx),
    width: offsetStyleValue(style.width, insetPx * 2),
    height: offsetStyleValue(style.height, insetPx * 2),
  };
}

const RESIZE_HANDLES: Array<{
  id: Exclude<TransformHandle, 'move'>;
  className: string;
  cursor: string;
}> = [
  { id: 'nw', className: '-left-1 -top-1', cursor: 'cursor-nwse-resize' },
  { id: 'n', className: 'left-1/2 -top-1 h-2 w-6 -translate-x-1/2 rounded-full', cursor: 'cursor-ns-resize' },
  { id: 'ne', className: '-top-1 -right-1', cursor: 'cursor-nesw-resize' },
  { id: 'e', className: '-right-1 top-1/2 h-6 w-2 -translate-y-1/2 rounded-full', cursor: 'cursor-ew-resize' },
  { id: 'se', className: '-bottom-1 -right-1', cursor: 'cursor-nwse-resize' },
  { id: 's', className: 'bottom-[-0.25rem] left-1/2 h-2 w-6 -translate-x-1/2 rounded-full', cursor: 'cursor-ns-resize' },
  { id: 'sw', className: '-bottom-1 -left-1', cursor: 'cursor-nesw-resize' },
  { id: 'w', className: '-left-1 top-1/2 h-6 w-2 -translate-y-1/2 rounded-full', cursor: 'cursor-ew-resize' },
];

const SelectionOverlay: React.FC<SelectionOverlayProps> = ({
  block,
  enabled,
  isSelected,
  isPrimarySelection = true,
  multiSelectActive = false,
  showControls = true,
  showResizeControls = true,
  muted = false,
  scaleMode = false,
  overlayStyle,
  overlayInsetPx = OVERLAY_INSET_PX,
  overlayRef,
  onBeginTransform,
  onMoveTransform,
  onEndTransform,
  onBeginScale,
  onScaleMove,
  onScaleEnd,
  onStartTextEdit,
  label,
}) => {
  if (!enabled || !isSelected) return null;

  const policy = resolveBlockPolicy(block);
  const canMove = policy.canMove;
  const canResize = policy.canResize;
  const resizeHandles = RESIZE_HANDLES;
  const expandedOverlayStyle = expandOverlayStyle(overlayStyle, overlayInsetPx);
  const selectionBorderClass = multiSelectActive
    ? isPrimarySelection
      ? 'border-2 border-blue-600 bg-blue-500/10 shadow-[0_0_0_1px_rgba(37,99,235,0.35)]'
      : 'border-2 border-dashed border-blue-500 bg-blue-400/14 shadow-[0_0_0_1px_rgba(59,130,246,0.22)]'
    : 'border border-blue-500/80';

  if (!showControls && !isSelected) return null;
  if (!showControls) {
    return (
      <div
        ref={overlayRef}
        className="pointer-events-none absolute"
        data-builder-selection-target={policy.selectionTarget}
        style={expandedOverlayStyle}
      >
        <div
          className={`pointer-events-none absolute z-20 rounded-xl ${selectionBorderClass} ${muted ? 'opacity-30' : ''}`}
          style={{ inset: 0 }}
        />
        {multiSelectActive && !isPrimarySelection ? (
          <div
            className={`pointer-events-none absolute right-0 top-0 z-40 translate-x-1 translate-y-[-110%] rounded-[6px] border border-blue-400/70 bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-white shadow-sm ${muted ? 'opacity-30' : ''}`}
          >
            Selected
          </div>
        ) : null}
        {label ? (
          <div
            className={`pointer-events-none absolute left-0 top-0 z-40 max-w-[18rem] -translate-y-[calc(100%+0.25rem)] rounded-[6px] border border-blue-400/70 bg-blue-600/92 px-2 py-0.5 text-[10px] font-semibold leading-tight text-white shadow-sm ${muted ? 'opacity-30' : ''}`}
          >
            <span className="block truncate">{label}</span>
          </div>
        ) : null}
      </div>
    );
  }

  if (!canMove && !canResize) return null;

  return (
    <div
      ref={overlayRef}
      className="pointer-events-none absolute"
      style={expandedOverlayStyle}
      data-builder-selection-target={policy.selectionTarget}
    >
      <div
        className={`pointer-events-none absolute z-20 rounded-xl ${selectionBorderClass} ${muted ? 'opacity-30' : ''}`}
        style={{ inset: 0 }}
      />
      {multiSelectActive && !isPrimarySelection ? (
        <div
          className={`pointer-events-none absolute right-0 top-0 z-40 translate-x-1 translate-y-[-110%] rounded-[6px] border border-blue-400/70 bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-white shadow-sm ${muted ? 'opacity-30' : ''}`}
        >
          Selected
        </div>
      ) : null}
      {canMove ? (
        <>
          {!scaleMode ? (
            <button
              type="button"
              className={`pointer-events-auto absolute inset-0 z-[22] cursor-grab bg-transparent active:cursor-grabbing ${muted ? 'opacity-0 pointer-events-none' : ''}`}
              aria-label="Move block"
              title="Drag to move. Shift: lock axis. Alt: disable snap. Ctrl+Alt+drag: duplicate."
              data-builder-allow-interaction
              data-builder-transform-handle="move"
              onPointerDown={(event) => onBeginTransform(event, block, 'move')}
              onPointerMove={onMoveTransform}
              onPointerUp={onEndTransform}
              onPointerCancel={onEndTransform}
              onDoubleClick={(event) => {
                if (block.type !== 'text' || !onStartTextEdit) return;
                event.preventDefault();
                event.stopPropagation();
                onStartTextEdit(block.id);
              }}
            />
          ) : null}
          <button
            type="button"
            className={`pointer-events-auto absolute left-0 right-0 top-0 z-[25] cursor-grab active:cursor-grabbing ${muted || scaleMode ? 'opacity-0 pointer-events-none' : 'bg-transparent'}`}
            style={{ height: `${FRAME_HIT_THICKNESS_PX}px` }}
            aria-label="Move block"
            title="Drag to move. Shift: lock axis. Alt: disable snap. Ctrl+Alt+drag: duplicate."
            data-builder-allow-interaction
            data-builder-transform-handle="move"
            onPointerDown={(event) => onBeginTransform(event, block, 'move')}
            onPointerMove={onMoveTransform}
            onPointerUp={onEndTransform}
            onPointerCancel={onEndTransform}
          />
          <button
            type="button"
            className={`pointer-events-auto absolute bottom-0 left-0 right-0 z-[25] cursor-grab active:cursor-grabbing ${muted || scaleMode ? 'opacity-0 pointer-events-none' : 'bg-transparent'}`}
            style={{ height: `${FRAME_HIT_THICKNESS_PX}px` }}
            aria-label="Move block"
            title="Drag to move. Shift: lock axis. Alt: disable snap. Ctrl+Alt+drag: duplicate."
            data-builder-allow-interaction
            data-builder-transform-handle="move"
            onPointerDown={(event) => onBeginTransform(event, block, 'move')}
            onPointerMove={onMoveTransform}
            onPointerUp={onEndTransform}
            onPointerCancel={onEndTransform}
          />
          <button
            type="button"
            className={`pointer-events-auto absolute bottom-0 left-0 top-0 z-[25] cursor-grab active:cursor-grabbing ${muted || scaleMode ? 'opacity-0 pointer-events-none' : 'bg-transparent'}`}
            style={{ width: `${FRAME_HIT_THICKNESS_PX}px` }}
            aria-label="Move block"
            title="Drag to move. Shift: lock axis. Alt: disable snap. Ctrl+Alt+drag: duplicate."
            data-builder-allow-interaction
            data-builder-transform-handle="move"
            onPointerDown={(event) => onBeginTransform(event, block, 'move')}
            onPointerMove={onMoveTransform}
            onPointerUp={onEndTransform}
            onPointerCancel={onEndTransform}
          />
          <button
            type="button"
            className={`pointer-events-auto absolute bottom-0 right-0 top-0 z-[25] cursor-grab active:cursor-grabbing ${muted || scaleMode ? 'opacity-0 pointer-events-none' : 'bg-transparent'}`}
            style={{ width: `${FRAME_HIT_THICKNESS_PX}px` }}
            aria-label="Move block"
            title="Drag to move. Shift: lock axis. Alt: disable snap. Ctrl+Alt+drag: duplicate."
            data-builder-allow-interaction
            data-builder-transform-handle="move"
            onPointerDown={(event) => onBeginTransform(event, block, 'move')}
            onPointerMove={onMoveTransform}
            onPointerUp={onEndTransform}
            onPointerCancel={onEndTransform}
          />
        </>
      ) : null}
      {scaleMode && onBeginScale
        ? (
            <>
              <button
                type="button"
                className={`${EDGE_HANDLE_CLASS} -right-1 top-1/2 h-6 w-2 -translate-y-1/2 cursor-ew-resize ${muted ? 'opacity-30' : ''}`}
                aria-label="Scale card width"
                title="Scale card width"
                data-builder-allow-interaction
                onPointerDown={(event) => onBeginScale(event, block, 'x')}
                onPointerMove={onScaleMove}
                onPointerUp={onScaleEnd}
                onPointerCancel={onScaleEnd}
              />
              <button
                type="button"
                className={`${EDGE_HANDLE_CLASS} bottom-[-0.25rem] left-1/2 h-2 w-6 -translate-x-1/2 cursor-ns-resize ${muted ? 'opacity-30' : ''}`}
                aria-label="Scale card height"
                title="Scale card height"
                data-builder-allow-interaction
                onPointerDown={(event) => onBeginScale(event, block, 'y')}
                onPointerMove={onScaleMove}
                onPointerUp={onScaleEnd}
                onPointerCancel={onScaleEnd}
              />
              <button
                type="button"
                className={`${CORNER_HANDLE_CLASS} -bottom-1 -right-1 cursor-nwse-resize ${muted ? 'opacity-30' : ''}`}
                aria-label="Scale card"
                title="Scale card"
                data-builder-allow-interaction
                onPointerDown={(event) => onBeginScale(event, block, 'both')}
                onPointerMove={onScaleMove}
                onPointerUp={onScaleEnd}
                onPointerCancel={onScaleEnd}
              >
                <MoveDiagonal2 className="h-3 w-3" />
              </button>
            </>
          )
        : null}
      {showResizeControls && canResize && !scaleMode
        ? resizeHandles.map((handle) => (
            <button
              key={handle.id}
              type="button"
              className={`${handle.id.length === 1 ? EDGE_HANDLE_CLASS : CORNER_HANDLE_CLASS} ${handle.className} ${handle.cursor} ${muted ? 'opacity-30' : ''}`}
              aria-label={`Resize block ${handle.id}`}
              title="Resize block"
              data-builder-allow-interaction
              data-builder-transform-handle={`resize-${handle.id}`}
              onPointerDown={(event) => onBeginTransform(event, block, handle.id)}
              onPointerMove={onMoveTransform}
              onPointerUp={onEndTransform}
              onPointerCancel={onEndTransform}
            />
          ))
        : null}
    </div>
  );
};

export default SelectionOverlay;
